import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Home, ArrowLeft, Search } from "lucide-react";
import Seo from "@/components/seo/Seo";
import { useEffect } from "react";

export default function NotFoundPage() {
  // Send 404 status hint to search crawlers (best-effort - SPAs can't truly send 404,
  // but this Noindex prevents the bogus URL from ranking)
  useEffect(() => {
    const robots = document.head.querySelector('meta[name="robots"]');
    const prev = robots?.getAttribute("content");
    if (robots) robots.setAttribute("content", "noindex, follow");
    return () => {
      if (robots && prev) robots.setAttribute("content", prev);
    };
  }, []);

  return (
    <div data-testid="not-found-page" className="page-transition min-h-[80vh] flex items-center justify-center bg-white px-4">
      <Seo
        title="Page Not Found | Antera Software"
        description="The page you're looking for doesn't exist. Browse our promotional products software solutions instead."
        path=""
      />
      <div className="max-w-xl w-full text-center py-16 lg:py-24">
        <p className="text-[120px] lg:text-[160px] font-extrabold leading-none tracking-tight text-transparent bg-clip-text bg-gradient-to-br from-sky-500 to-sky-700">
          404
        </p>
        <h1 className="mt-2 text-2xl sm:text-3xl font-bold text-slate-900">
          We couldn't find that page
        </h1>
        <p className="mt-3 text-sm sm:text-base text-slate-500">
          The page may have moved, or the link you followed may be broken. Let's get you back on track.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/">
            <Button data-testid="not-found-home-btn" className="bg-sky-600 hover:bg-sky-700 text-white rounded-full px-6 h-11 text-sm font-semibold">
              <Home className="mr-2 h-4 w-4" /> Back to Home
            </Button>
          </Link>
          <Link to="/solutions">
            <Button variant="outline" data-testid="not-found-solutions-btn" className="rounded-full px-6 h-11 text-sm font-semibold border-slate-300 text-slate-700 hover:bg-slate-50">
              Explore Solutions
            </Button>
          </Link>
        </div>

        <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-3 text-left">
          {[
            { label: "Solutions", path: "/solutions" },
            { label: "Products", path: "/products" },
            { label: "Blog", path: "/blog" },
            { label: "Contact", path: "/contact" },
          ].map((l) => (
            <Link
              key={l.path}
              to={l.path}
              className="group bg-slate-50 hover:bg-sky-50 border border-slate-200 hover:border-sky-200 rounded-lg px-4 py-3 transition-colors"
            >
              <span className="text-sm font-semibold text-slate-700 group-hover:text-sky-700 inline-flex items-center gap-1.5">
                {l.label}
                <ArrowLeft className="h-3.5 w-3.5 rotate-180 opacity-0 group-hover:opacity-100 transition-opacity" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
